import { useTranslation } from 'next-i18next';
import React, { ReactElement, useRef, useState } from 'react';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import {
  FdrCustomMapControl,
  FdrMapControlPosition,
  FdrMapProvider,
} from '@hotelplan/fdr.regular.fusion.fdr-map';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useElementSize, useViewportSize } from '@hotelplan/libs.hooks-dom';
import { FdrHotel } from '@hotelplan/supergraph-api';
import {
  createFullScreenControl,
  productFdrMapOptions,
  useFullScreenControl,
} from 'components/domain/fdr-map';
import { useFdrHotelCoordinatesQuery } from 'schemas/query/hotel/fdr-hotel-coordinates.generated';
import {
  FdrHotelMapItemFragment,
  FdrNearestHotelsFragment,
  useFdrNearestHotelsQuery,
} from 'schemas/query/hotel/fdr-nearest-hotels.generated';
import {
  FdrNearestHotelsMapPins,
  FdrSingleHotelMapPin,
} from './fdr-hotel-map-pin';
import {
  PRODUCT_GALLERY_MAP_ZOOM,
  SCREEN_SIZE_TO_ENABLE_MIN_MAP_HEIGHT,
} from './fdr-hotel-map.constants';
import {
  createNearHotelsControl,
  createSpinnerControl,
} from './fdr-hotel-map.controls';
import {
  FdrHotelMapContainer,
  FdrHotelMapSection,
} from './fdr-hotel-map.styles';

export function FdrHotelMap(): ReactElement {
  const [t] = useTranslation('pdp');
  const { id } = useObjectId();
  const { channelType } = useAuthentication();

  const [showNearHotels, setShowNearHotels] = useState<boolean>(false);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapSize = useElementSize(mapRef, { enable: true });
  const { setIsFullScreen } = useFullScreenControl(mapRef);

  const { desktop } = useDeviceType();
  const { width } = useViewportSize();

  const fdrHotelCoordsQuery = useFdrHotelCoordinatesQuery({
    variables: { id },
  });

  const fdrNearestHotelsQuery = useFdrNearestHotelsQuery({
    variables: { id },
    skip: !showNearHotels,
  });

  const loading =
    fdrHotelCoordsQuery?.loading || fdrNearestHotelsQuery?.loading;

  const fdrNearestHotels = (
    fdrNearestHotelsQuery?.data?.fdrProduct as FdrNearestHotelsFragment
  )?.resort?.products?.productsPage.filter(
    i => i.__typename === 'FdrHotel' // Prevents the query from returning not hotels
  ) as Array<FdrHotelMapItemFragment>;

  const activeFdrHotelCoords = (
    fdrHotelCoordsQuery?.data?.fdrProduct as unknown as FdrHotel
  )?.coordinates;

  return (
    <FdrHotelMapContainer>
      <FdrHotelMapSection
        ref={mapRef}
        desktop={desktop}
        isFixedHeight={width && width < SCREEN_SIZE_TO_ENABLE_MIN_MAP_HEIGHT}
      >
        <FdrMapProvider
          styles={{
            width: `${mapSize.w}px`,
            height: `${mapSize.h}px`,
          }}
          zoom={PRODUCT_GALLERY_MAP_ZOOM}
          options={productFdrMapOptions}
        >
          {fdrNearestHotels?.length ? (
            <FdrNearestHotelsMapPins
              nearestHotels={fdrNearestHotels}
              activeFdrHotelCoords={activeFdrHotelCoords}
            />
          ) : (
            <FdrSingleHotelMapPin coordinates={activeFdrHotelCoords} />
          )}
          {channelType === AuthChannelType.B2B ? (
            <>
              {loading ? (
                <FdrCustomMapControl
                  onCreateControl={() => createSpinnerControl()}
                />
              ) : null}
              <FdrCustomMapControl
                onCreateControl={() =>
                  createNearHotelsControl({
                    value: showNearHotels,
                    onChange: setShowNearHotels,
                    title: t('pdp:show.near.hotels'),
                  })
                }
              />
            </>
          ) : null}
          <FdrCustomMapControl
            onCreateControl={createFullScreenControl({
              onChange: setIsFullScreen,
            })}
            position={FdrMapControlPosition.RIGHT_TOP}
          />
        </FdrMapProvider>
      </FdrHotelMapSection>
    </FdrHotelMapContainer>
  );
}
